/**
 * The local archive: one record per message.
 *
 * It began as a cache (astrocall-8kx) — the server held the plaintext, and this
 * saved a round trip. E2EE changed what it is without changing what it was
 * called: message keys are destroyed on use, so the server's copy can be read
 * exactly once, and this is the ONLY place a conversation exists afterwards.
 * A cache that loses an entry costs a request; an archive that loses one costs
 * the message.
 *
 * That is why the shape changed. The first version kept a whole conversation as
 * one blob under one key, capped at 500 messages — a sensible eviction bound
 * for a cache, and a silent deletion policy for an archive: message 501 did not
 * "fall out of cache", it ceased to exist. Every write also rewrote the entire
 * conversation, so a chatty thread grew slower with each message and risked
 * taking the whole history down with one bad write.
 *
 * Now each message is its own record, keyed by (conversation, id), with an
 * index on the conversation. Writing one message writes one record. Nothing is
 * evicted: the bound is the device's disk, and clearing it is a decision a
 * person makes, not a constant.
 *
 * The backend is injected so all of this is testable under node --test, where
 * IndexedDB does not exist.
 */

/**
 * No longer a cap — kept because it appears in older code paths and in the
 * spec, and because the number itself is part of the story (`astrocall-d11`).
 * Nothing enforces it any more.
 */
export const MAX_CACHED_PER_CONVERSATION = 500;

const OUTBOX_KEY = '__outbox__';
const OUTBOX_MAX = 200;
const ATTACH_PREFIX = '__attach__:';
const META_PREFIX = '__meta__:';

/** Stable conversation id for a pair of users — order must not matter. */
export function conversationId(myId, peerId) {
  const a = Number(myId), b = Number(peerId);
  return a < b ? `${a}:${b}` : `${b}:${a}`;
}

/**
 * Merge incoming messages into held ones: dedupe by id, keep chronological
 * order, and let a readable copy win over one that could not be opened.
 *
 * Ordering is by id, not created_at: timestamps come from two clocks (server
 * insert time and the optimistic local echo) and ties are common inside one
 * second, which made messages swap places on reload.
 */
export function mergeMessages(existing = [], incoming = []) {
  const byId = new Map();
  for (const m of existing) if (m && m.id != null) byId.set(m.id, m);
  for (const m of incoming) {
    if (!m || m.id == null) continue;
    const prev = byId.get(m.id);
    // A copy that did not open must never replace one that did. The ratchet
    // destroys a message key the moment it is used, so a SECOND attempt at a
    // row this device already opened cannot succeed — it is not evidence that
    // anything is wrong. Letting it win rewrote read history into "не удалось
    // расшифровать" one page at a time, every time somebody scrolled up.
    if (prev && m.decryptError && !prev.decryptError) {
      byId.set(m.id, { ...prev, read: m.read != null ? m.read : prev.read });
      continue;
    }
    // Otherwise the server copy wins: it carries read state and resolved
    // file/reply metadata.
    const next = { ...prev, ...m };
    // Spreading cannot clear a flag the newer copy simply does not carry, so a
    // row that failed once stayed marked broken even after it opened.
    if (!m.decryptError) delete next.decryptError;
    byId.set(m.id, next);
  }

  // A resend arrives as a NEW row carrying the SAME message: the envelope
  // keeps the original id while the row gets a fresh one, because the server
  // would otherwise collapse the resend onto the row that could not be opened.
  // So the same message can be here twice — once unreadable, once readable —
  // and showing both would be showing the failure next to its own repair.
  const readable = new Map();
  for (const m of byId.values()) {
    if (m.client_mid && !m.decryptError) readable.set(m.client_mid, m);
  }
  for (const [id, m] of byId) {
    if (m.decryptError && m.client_mid && readable.has(m.client_mid)) byId.delete(id);
  }

  return [...byId.values()].sort((a, b) => a.id - b.id);
}

/** Highest message id we already hold — the sync cursor. */
export function lastId(messages = []) {
  let max = 0;
  for (const m of messages) if (m && m.id > max) max = m.id;
  return max;
}

/** Lowest message id — the cursor for paging further back. */
export function firstId(messages = []) {
  let min = Infinity;
  for (const m of messages) if (m && m.id != null && m.id < min) min = m.id;
  return Number.isFinite(min) ? min : 0;
}

// ─── Backends ───────────────────────────────────────────────────────────────
//
// Two halves, because they have different shapes: messages are many small
// records read by conversation, everything else (read position, the outbox,
// attachment bytes) is a handful of values read by name.

/** In-memory backend — used by tests and as a fallback when IDB is blocked. */
export function memoryBackend() {
  const messages = new Map();   // conv → Map<id, row>
  const values = new Map();

  return {
    async getMessages(conv) {
      const held = messages.get(conv);
      return held ? [...held.values()].sort((a, b) => a.id - b.id) : [];
    },
    async putMessages(conv, rows) {
      if (!messages.has(conv)) messages.set(conv, new Map());
      const held = messages.get(conv);
      for (const r of rows) if (r && r.id != null) held.set(r.id, r);
    },
    async deleteMessages(conv, ids) {
      const held = messages.get(conv);
      if (held) for (const id of ids) held.delete(id);
    },
    async clearConversation(conv) { messages.delete(conv); },
    async conversationIds() { return [...messages.keys()]; },
    async getValue(key) { return values.has(key) ? values.get(key) : null; },
    async setValue(key, v) { values.set(key, v); },
    async deleteValue(key) { values.delete(key); },
    async valueKeys(prefix = '') {
      return [...values.keys()].filter(k => k.startsWith(prefix));
    },
  };
}

const DB_NAME = 'voxiox';
const DB_VERSION = 2;
const LEGACY_STORE = 'conversations';
const MSG_STORE = 'messages';
const KV_STORE = 'kv';

/** IndexedDB backend. Returns null when unavailable (private mode, old WebView). */
export function idbBackend() {
  if (typeof indexedDB === 'undefined') return null;

  let dbPromise = null;
  function open() {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = (event) => {
        const db = req.result;
        if (!db.objectStoreNames.contains(MSG_STORE)) {
          const store = db.createObjectStore(MSG_STORE, { keyPath: ['conv', 'id'] });
          store.createIndex('conv', 'conv');
        }
        if (!db.objectStoreNames.contains(KV_STORE)) db.createObjectStore(KV_STORE);

        // Move what the blob-per-conversation version wrote. Done inside the
        // upgrade transaction, so a device either comes up on the new shape
        // with its history or stays on the old one — never half-migrated.
        if (event.oldVersion < 2 && db.objectStoreNames.contains(LEGACY_STORE)) {
          const legacy = req.transaction.objectStore(LEGACY_STORE);
          const msgs = req.transaction.objectStore(MSG_STORE);
          const kv = req.transaction.objectStore(KV_STORE);
          const cursorReq = legacy.openCursor();
          cursorReq.onsuccess = () => {
            const cursor = cursorReq.result;
            if (!cursor) return;
            const key = cursor.key;
            const raw = cursor.value;
            if (typeof key === 'string' && raw && typeof raw === 'object' && Array.isArray(raw.messages)) {
              for (const m of raw.messages) {
                if (m && m.id != null) msgs.put({ ...m, conv: key, id: m.id });
              }
              if (raw.meta && Object.keys(raw.meta).length) kv.put(raw.meta, META_PREFIX + key);
            } else if (typeof key === 'string') {
              kv.put(raw, key);          // outbox and attachments kept as they were
            }
            cursor.continue();
          };
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    return dbPromise;
  }

  function run(store, mode, fn) {
    return open().then(db => new Promise((resolve, reject) => {
      const t = db.transaction(store, mode);
      const req = fn(t.objectStore(store));
      if (req && 'onsuccess' in req) {
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      } else {
        t.oncomplete = () => resolve();
        t.onerror = () => reject(t.error);
      }
    }));
  }

  return {
    async getMessages(conv) {
      try {
        const rows = await run(MSG_STORE, 'readonly', s => s.index('conv').getAll(conv));
        return (rows || []).sort((a, b) => a.id - b.id);
      } catch (e) { return []; }
    },
    async putMessages(conv, rows) {
      try {
        await run(MSG_STORE, 'readwrite', (s) => {
          for (const r of rows) if (r && r.id != null) s.put({ ...r, conv, id: r.id });
          return null;
        });
      } catch (e) { /* quota or blocked */ }
    },
    async deleteMessages(conv, ids) {
      try {
        await run(MSG_STORE, 'readwrite', (s) => {
          for (const id of ids) s.delete([conv, id]);
          return null;
        });
      } catch (e) {}
    },
    async clearConversation(conv) {
      try {
        const rows = await this.getMessages(conv);
        await this.deleteMessages(conv, rows.map(r => r.id));
      } catch (e) {}
    },
    async conversationIds() {
      try {
        const keys = await run(MSG_STORE, 'readonly', s => s.index('conv').getAllKeys());
        return [...new Set(keys || [])];
      } catch (e) { return []; }
    },
    async getValue(key) {
      try { return (await run(KV_STORE, 'readonly', s => s.get(key))) ?? null; } catch (e) { return null; }
    },
    async setValue(key, v) {
      try { await run(KV_STORE, 'readwrite', s => s.put(v, key)); } catch (e) {}
    },
    async deleteValue(key) {
      try { await run(KV_STORE, 'readwrite', s => s.delete(key)); } catch (e) {}
    },
    async valueKeys(prefix = '') {
      try {
        const keys = await run(KV_STORE, 'readonly', s => s.getAllKeys());
        return (keys || []).filter(k => typeof k === 'string' && k.startsWith(prefix));
      } catch (e) { return []; }
    },
  };
}

// ─── Store ──────────────────────────────────────────────────────────────────

/**
 * The cache must never be able to block the UI. IndexedDB can hang instead of
 * failing — blocked by another tab, disabled in the embedding WebView, or stuck
 * behind a pending upgrade — and a request that never settles would leave the
 * conversation empty forever.
 */
export function withTimeout(promise, ms, fallback) {
  return new Promise((resolve) => {
    let done = false;
    const timer = setTimeout(() => { if (!done) { done = true; resolve(fallback); } }, ms);
    Promise.resolve(promise).then(
      (v) => { if (!done) { done = true; clearTimeout(timer); resolve(v); } },
      () => { if (!done) { done = true; clearTimeout(timer); resolve(fallback); } },
    );
  });
}

export const CACHE_TIMEOUT_MS = 500;

/**
 * The archive, as one object for the whole app.
 *
 * It used to be created inside the dashboard, which was fine while the
 * dashboard was the only reader. The export screen is a second one, and two
 * MessageStore instances over the same IndexedDB would each cache their own
 * idea of a conversation — so the file somebody saved could differ from what
 * they had just been reading.
 */
export class MessageStore {
  constructor(backend) {
    this.backend = backend || idbBackend() || memoryBackend();
  }

  /** Cached messages, oldest first. Empty array when nothing is cached yet. */
  async getMessages(convId) {
    return this.backend.getMessages(convId);
  }

  /**
   * Merge new messages in and return the conversation.
   *
   * Only what changed is written. The blob version rewrote every message in the
   * conversation on every arrival, which is why a long thread got slower with
   * each message — and why one failed write could take the whole history with
   * it.
   */
  async putMessages(convId, incoming) {
    const rows = (incoming || []).filter(m => m && m.id != null);
    if (!rows.length) return this.getMessages(convId);

    const held = await this.backend.getMessages(convId);
    const merged = mergeMessages(held, rows);

    const before = new Map(held.map(m => [m.id, m]));
    const after = new Map(merged.map(m => [m.id, m]));
    const changed = merged.filter(m => before.get(m.id) !== m);
    const removed = held.filter(m => !after.has(m.id)).map(m => m.id);

    if (changed.length) await this.backend.putMessages(convId, changed);
    if (removed.length) await this.backend.deleteMessages(convId, removed);
    return merged;
  }

  /** Replace the conversation wholesale (used when restoring an archive). */
  async replaceMessages(convId, list) {
    await this.backend.clearConversation(convId);
    const rows = mergeMessages([], list || []);
    if (rows.length) await this.backend.putMessages(convId, rows);
    return rows;
  }

  async getMeta(convId) {
    return (await this.backend.getValue(META_PREFIX + convId)) || {};
  }

  /** Remember where the user stopped reading, so the chat reopens there. */
  async setMeta(convId, patch) {
    const next = { ...(await this.getMeta(convId)), ...patch };
    await this.backend.setValue(META_PREFIX + convId, next);
    return next;
  }

  async clear(convId) {
    await this.backend.clearConversation(convId);
    await this.backend.deleteValue(META_PREFIX + convId);
  }

  /** Every conversation this device holds, by id. */
  async conversationIds() {
    return this.backend.conversationIds();
  }

  /**
   * The outbox: messages composed but not yet acknowledged by the server.
   *
   * `wsService.send` drops anything handed to it while the socket is down —
   * quietly, because there is nowhere to put it. So a message written on a
   * phone with no signal lived in React state and nowhere else: it looked sent,
   * and the next restart erased it. The ciphertext is already built by then, so
   * what is kept here is exactly what has to go on the wire, and re-sending it
   * is safe: the server collapses a repeat onto the same row by client_mid.
   */
  async getOutbox() {
    const raw = await this.backend.getValue(OUTBOX_KEY);
    return Array.isArray(raw?.entries) ? raw.entries : [];
  }

  async putOutbox(entry) {
    const entries = (await this.getOutbox()).filter(e => e.clientMid !== entry.clientMid);
    entries.push(entry);
    await this.backend.setValue(OUTBOX_KEY, { entries: entries.slice(-OUTBOX_MAX) });
    return entries;
  }

  async dropOutbox(clientMid) {
    const entries = (await this.getOutbox()).filter(e => e.clientMid !== clientMid);
    await this.backend.setValue(OUTBOX_KEY, { entries });
    return entries;
  }

  /**
   * Attachment bytes, kept because the server's copy is not forever.
   *
   * A restored conversation whose pictures are all gone is the quiet failure
   * the export exists to avoid, and after a phone change the ciphertext they
   * came in is unreadable anyway — so the archive carries the bytes, and this
   * is where they land when it is read back.
   */
  async putAttachment(fileId, base64) {
    await this.backend.setValue(ATTACH_PREFIX + fileId, { b64: base64, at: Date.now() });
  }

  async getAttachment(fileId) {
    const held = await this.backend.getValue(ATTACH_PREFIX + fileId);
    return held && held.b64 ? held.b64 : null;
  }

  /** Values under a name, for anything that is not a message (jobs, state). */
  async getValue(key) { return this.backend.getValue(key); }
  async setValue(key, v) { return this.backend.setValue(key, v); }
  async deleteValue(key) { return this.backend.deleteValue(key); }
  async valueKeys(prefix = '') { return this.backend.valueKeys(prefix); }
}

/** The one the app uses. Tests build their own with an explicit backend. */
export const messageStore = new MessageStore();
